import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faTriangleExclamation,
  faCircleQuestion,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";

import { bomLineFlagConfig, summarizeFile } from "./mockBomData";

const flagIconMap = {
  reported: faCircleCheck,
  assumption: faTriangleExclamation,
  insufficient: faCircleQuestion
};

const FlagChip = ({ flag }) => {
  const { palette } = useTheme();
  const config = bomLineFlagConfig[flag];

  if (!config) {
    return null;
  }

  const { colorKey, label, tooltip } = config;

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        icon={
          <FontAwesomeIcon
            icon={flagIconMap[flag]}
            style={{ color: palette[colorKey].main }}
          />
        }
        label={label}
        sx={{
          color: palette[colorKey].main,
          borderColor: palette[colorKey].main,
          backgroundColor: "transparent"
        }}
        variant="outlined"
      />
    </Tooltip>
  );
};

const WeightCell = ({ line, onEditWeight }) => {
  const { weightGrams, percent, weightSource } = line;

  if (weightGrams != null) {
    return <Typography variant="body2">{weightGrams} g</Typography>;
  }

  if (percent != null) {
    return <Typography variant="body2">{percent}%</Typography>;
  }

  if (weightSource === "missing") {
    return (
      <Typography variant="body2" color="error">
        Needs input
      </Typography>
    );
  }

  return (
    <TextField
      size="small"
      type="number"
      value={weightGrams ?? ""}
      onChange={(event) => onEditWeight(Number(event.target.value))}
      InputProps={{ endAdornment: <Typography variant="caption">g</Typography> }}
      sx={{ maxWidth: 110 }}
    />
  );
};

const MaterialTypeCell = ({ materialType }) => {
  if (materialType) {
    return <Typography variant="body2">{materialType}</Typography>;
  }

  return (
    <Typography variant="body2" color="error">
      Unmapped material
    </Typography>
  );
};

const BomLineRow = ({ line, onEditWeight }) => {
  const { rawName, section, rawComposition } = line;

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{rawName}</Typography>
        <Typography variant="caption" color="textSecondary">
          {section}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="textSecondary">
          {rawComposition || "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <MaterialTypeCell materialType={line.materialType} />
      </TableCell>
      <TableCell>
        <WeightCell line={line} onEditWeight={onEditWeight} />
      </TableCell>
      <TableCell>
        <FlagChip flag={line.flag} />
      </TableCell>
    </TableRow>
  );
};

const FlagCountChip = ({ flag, count }) => {
  const { palette } = useTheme();

  if (!count) {
    return null;
  }

  const { colorKey, label } = bomLineFlagConfig[flag];

  return (
    <Chip
      size="small"
      icon={
        <FontAwesomeIcon
          icon={flagIconMap[flag]}
          style={{ color: palette[colorKey].main }}
        />
      }
      label={`${count} ${label.toLowerCase()}`}
      variant="outlined"
      sx={{ color: palette[colorKey].main, borderColor: palette[colorKey].main }}
    />
  );
};

const ProductReviewCard = ({ file, onEditLineWeight }) => {
  const { palette } = useTheme();
  const { sourceFile, detectedTemplate, parseConfidence, product } = file;
  const allLines = [...(file.lines || []), ...(file.packaging || [])];

  const isLowConfidence = parseConfidence < 0.6;
  const { reportedCount, assumptionCount, insufficientCount } =
    summarizeFile(file);
  const needsAttention = isLowConfidence || insufficientCount > 0;

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        mb: 1,
        border: `1px solid ${
          needsAttention ? palette.error.light : palette.backgroundGray.dark
        }`,
        "&:before": { display: "none" }
      }}
    >
      <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
            gap: 1,
            pr: 1
          }}
        >
          <Box>
            <Typography variant="subtitle1">
              {product.name} ({product.sku})
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {sourceFile} • {detectedTemplate} •{" "}
              {Math.round(parseConfidence * 100)}% confidence
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <FlagCountChip flag="reported" count={reportedCount} />
            <FlagCountChip flag="assumption" count={assumptionCount} />
            <FlagCountChip flag="insufficient" count={insufficientCount} />
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {isLowConfidence ? (
          <Typography variant="body2" color="error">
            Low parse confidence — this product needs manual review before
            import.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell>Composition</TableCell>
                <TableCell>Material type</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allLines.map((line, index) => (
                <BomLineRow
                  key={`${line.rawName}-${index}`}
                  line={line}
                  onEditWeight={(weight) =>
                    onEditLineWeight(sourceFile, index, weight)
                  }
                />
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const BomReviewTable = ({ files, onEditLineWeight }) => (
  <Box>
    {files.map((file) => (
      <ProductReviewCard
        key={file.sourceFile}
        file={file}
        onEditLineWeight={onEditLineWeight}
      />
    ))}
  </Box>
);

export default BomReviewTable;
